import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Fade,
  Chip,
  alpha,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Sunrise, Sunset, History, LucideIcon } from 'lucide-react';
import { RitualTone, toneGradient } from './ritualStyles';

const TONE_ICON: Record<RitualTone, LucideIcon> = {
  plan: Sunrise,
  eod: Sunset,
  retro: History
};

/**
 * The app's AppBar is fixed at z-index 1400 (layout/Header.tsx) — above MUI's
 * default modal z-index of 1300. Without lifting the drawer above it, the header
 * bar covers the top of the gradient (clipping the greeting and the close button)
 * AND stays clickable through the backdrop, so the blocking gate wouldn't
 * actually block: search, notifications and the avatar menu would remain reachable.
 */
const ABOVE_APP_BAR_Z = 1500;

export interface RitualDrawerProps {
  open: boolean;
  tone: RitualTone;
  /** Small uppercase pill above the title, e.g. "CATCHING UP". */
  eyebrow?: string;
  title: string;
  subtitle?: string;
  /** 0-100. Fills the bar under the header as decisions land. */
  progress: number;
  /** Live counter, shown at the left of the footer. */
  footerNote?: React.ReactNode;
  primaryAction: React.ReactNode;
  /**
   * Way out when the primary action cannot succeed (server down, stale state).
   * Rendered ONLY in blocking mode — see the note on the footer below.
   */
  recovery?: React.ReactNode;
  /** Absent => BLOCKING: no Esc, no backdrop close, no Cancel, no close button. */
  onClose?: () => void;
  /** When set, replaces the body+footer with the completion moment. */
  success?: { title: string; subtitle?: string } | null;
  children: React.ReactNode;
}

/**
 * The shared shell for all three daily-ritual surfaces.
 *
 * A right-side drawer, deliberately generous (full width on mobile, 560-680px on
 * desktop) so task cards and reason fields get the same room a dialog had.
 *
 * Blocking mode leans on MUI's built-in focus trap: pass no `onClose` and never
 * set `disableEnforceFocus`. Combined with `disableEscapeKeyDown`, the surface is
 * genuinely inescapable — which is the intent, not an oversight.
 */
const RitualDrawer: React.FC<RitualDrawerProps> = ({
  open,
  tone,
  eyebrow,
  title,
  subtitle,
  progress,
  footerNote,
  primaryAction,
  recovery,
  onClose,
  success,
  children
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const blocking = !onClose;
  const ToneIcon = TONE_ICON[tone];
  const gradient = toneGradient(tone, isDark);

  const headingId = React.useId();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      disableEscapeKeyDown={blocking}
      aria-labelledby={headingId}
      sx={{ zIndex: ABOVE_APP_BAR_Z }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha('#000', 0.5),
            backdropFilter: 'blur(4px)'
          }
        },
        paper: {
          sx: {
            width: { xs: '100%', sm: 560, md: 680 },
            maxWidth: '100%',
            borderTopLeftRadius: { xs: 0, sm: 16 },
            borderBottomLeftRadius: { xs: 0, sm: 16 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundImage: 'none'
          }
        }
      }}
    >
      {/* ── Header ─────────────────────────────────────────────── */}
      <Box
        sx={{
          px: { xs: 2.5, sm: 4 },
          py: { xs: 3, sm: 3.5 },
          background: gradient,
          color: '#fff',
          // The theme sets typography.allVariants.color (styles/theme.ts), which
          // hard-codes a colour on EVERY <Typography> — dark navy in light mode. That
          // beats the inherited '#fff' above, so without this the title and subtitle
          // render navy-on-gradient. Any coloured surface in this app has to say so
          // explicitly; inheritance alone will not reach Typography.
          '& .MuiTypography-root': { color: '#fff' },
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          position: 'relative',
          flexShrink: 0
        }}
      >
        {onClose && (
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: 'rgba(255,255,255,0.85)',
              '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: onClose ? 5 : 0 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              flexShrink: 0,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.16)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ToneIcon size={28} color="#fff" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {eyebrow && (
              <Chip
                label={eyebrow}
                size="small"
                sx={{
                  mb: 0.75,
                  height: 20,
                  bgcolor: 'rgba(255,255,255,0.18)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  letterSpacing: '0.08em'
                }}
              />
            )}
            <Typography
              id={headingId}
              variant="h5"
              component="h1"
              tabIndex={-1}
              sx={{ fontWeight: 700, fontSize: { xs: '1.35rem', sm: '1.5rem' }, outline: 'none' }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {!success && (
          <Box
            sx={{
              mt: 2.5,
              height: 4,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.15)',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                height: '100%',
                borderRadius: 2,
                // Deliberately not solid white: a 100% bar should read as a filled
                // bar, not as a hard rule under the header.
                bgcolor: 'rgba(255,255,255,0.55)',
                width: `${Math.min(100, Math.max(0, progress))}%`,
                transition: 'width 0.4s ease'
              }}
            />
          </Box>
        )}
      </Box>

      {/* ── Completion moment ──────────────────────────────────── */}
      {success ? (
        <Fade in timeout={300}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              px: 4,
              gap: 1
            }}
          >
            <CheckCircleIcon
              sx={{ fontSize: 72, color: 'success.main', mb: 1 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {success.title}
            </Typography>
            {success.subtitle && (
              <Typography variant="body2" color="text.secondary">
                {success.subtitle}
              </Typography>
            )}
          </Box>
        </Fade>
      ) : (
        <>
          {/* ── Body ───────────────────────────────────────────── */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: { xs: 2.5, sm: 4 },
              py: 3
            }}
          >
            {children}
          </Box>

          {/* ── Sticky footer ──────────────────────────────────── */}
          <Box
            sx={{
              flexShrink: 0,
              px: { xs: 2.5, sm: 4 },
              py: 2,
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            <Box aria-live="polite" sx={{ minWidth: 0 }}>
              {typeof footerNote === 'string' ? (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {footerNote}
                </Typography>
              ) : (
                footerNote
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {/* Blocking mode has no Esc, no backdrop and no Cancel — so if the
                  primary action can't succeed, the only button on screen is a dead
                  end and the user is locked out of the whole app. The caller passes
                  `recovery` once it has an error to recover from. */}
              {blocking && recovery}
              {onClose && (
                <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none' }}>
                  Not now
                </Button>
              )}
              {primaryAction}
            </Box>
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default RitualDrawer;
